import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { KpiNumber } from "@/components/ui/kpi-number";
import { EstadoDoValor } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePickerWithRange } from "@/components/ui/date-picker";
import { CarregandoConteudo } from "@/components/ui/carregando-conteudo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  StandardizedTable,
  TableColumn,
} from "@/components/ui/standardized-table";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import {
  Target,
  DollarSign,
  Eye,
  MousePointer,
  TrendingUp,
  TrendingDown,
  Users,
  Share2,
  Clock,
  RefreshCw,
  AlertTriangle,
  BarChart3,
  Facebook,
} from "lucide-react";
import {
  LineChart as TremorLineChart,
  DonutChart as TremorDonutChart,
} from "@tremor/react";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import {
  getConnectionStatusApiV1IntegrationsMetaAdsConnectionStatusGet,
  getAccountsApiV1IntegrationsMetaAdsAccountsGet,
  getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet,
  getIntegrationAuthUrlApiV1IntegrationsNexusAuthUrlGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  MetaAdsAccount,
  MetaAdsCompleteResponse,
} from "@/backend-sdk/client/types.gen";
import IntegrationErrorBoundary from "@/components/common/ErrorBoundary";
import { useIntegrationStatus } from "@/hooks/useIntegrationStatus";
import { Input } from "@/components/ui/input";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { formatSignedPercent } from "@/utils/numberFormat";

type MetaTimeSeriesDay = {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  reach: number;
};

type MetaDemographicRow = {
  demographic: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
};

type MetaPlacementRow = {
  name: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
};

type MetaDailyMetric = {
  date: string;
  cost?: number | null;
  impressions?: number | null;
  clicks?: number | null;
  conversions?: number | null;
  reach?: number | null;
  roas?: number | null;
};

type MetaDemographicMetric = {
  age?: string | null;
  gender?: string | null;
  cost?: number | null;
  impressions?: number | null;
  clicks?: number | null;
  conversions?: number | null;
};

type MetaInterestMetric = {
  impressions?: number | null;
};

type MetaPlacementMetric = {
  platform_position?: string | null;
  cost?: number | null;
  impressions?: number | null;
  clicks?: number | null;
  conversions?: number | null;
};

/** Os quatro cards somam as métricas diárias da conta do Meta Ads. */
const FONTES_DOS_CARDS: readonly RequisitoDeFonte[] = ["meta_ads"];

const MetaAdsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<MetaAdsAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [completeData, setCompleteData] =
    useState<MetaAdsCompleteResponse | null>(null);
  // Começa ligado: a busca inicial sai no efeito de montagem, e a primeira pintura já é dela.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // `error` também carrega "nenhum dado no período", que não é falha; o motivo do N/D precisa
  // distinguir as duas coisas.
  const [falhaNasMetricas, setFalhaNasMetricas] = useState(false);
  const [campaignNameInclude, setCampaignNameInclude] = useState("");
  const [campaignNameExclude, setCampaignNameExclude] = useState("");
  const [dateRange, setDateRange] = useState({
    from: new Date("2025-06-23"), // Data inicial dos dados reais
    to: new Date("2025-07-23"), // Data final dos dados reais
  });
  const { filter: globalFilter } = useGlobalFilter();

  const {
    status,
    health,
    isLoading: statusLoading,
    forceCheck,
  } = useIntegrationStatus("meta_ads", "meta-ads-main");

  // Calcular período padrão (período com dados no seed: 2025-06-23 até 2025-07-23)
  const getDefaultDateRange = () => {
    return {
      startDate: "2025-06-23",
      endDate: "2025-07-23",
    };
  };

  // Carregar dados iniciais
  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Verificar status da conexão
      const { data: statusResult } =
        await getConnectionStatusApiV1IntegrationsMetaAdsConnectionStatusGet({
          throwOnError: true,
        });

      if (statusResult.is_connected) {
        // Carregar contas
        const { data: accountsData } =
          await getAccountsApiV1IntegrationsMetaAdsAccountsGet({
            throwOnError: true,
          });
        setAccounts(accountsData);

        if (accountsData.length > 0 && !selectedAccount) {
          setSelectedAccount(accountsData[0].id);
        }
      }
    } catch (err) {
      console.error("Erro ao carregar dados iniciais:", err);
      setError("Erro ao carregar dados do Meta Ads");
    } finally {
      setLoading(false);
    }
  };

  const loadCompleteData = async () => {
    if (!selectedAccount) return;

    try {
      setLoading(true);
      setError(null);

      // Usar período onde realmente existem dados
      const { startDate, endDate } = getDefaultDateRange();

      const { data } =
        await getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet({
          query: {
            account_id: selectedAccount,
            start_date: startDate,
            end_date: endDate,
          },
          throwOnError: true,
        });

      // Filtrar por nome de campanha (frontend side por enquanto, já que o backend retorna tudo)
      if (
        data &&
        data.daily_metrics &&
        (campaignNameInclude || campaignNameExclude)
      ) {
        // Nota: daily_metrics pode não ter nome da campanha diretamente, dependendo da estrutura.
        // Se a API não retornar nome da campanha em daily_metrics, a filtragem precisa ser no backend.
        // Verificamos anteriormente que MetaAdsMetrics não tem campaign_name.
        // Se não tiver, não podemos filtrar aqui sem enrich.
        // Mas a estrutura completa pode ter.
      }

      setCompleteData(data);
      setFalhaNasMetricas(false);

      if (!data.daily_metrics || data.daily_metrics.length === 0) {
        setError("Nenhum dado encontrado para o período selecionado");
      }
    } catch (err) {
      setFalhaNasMetricas(true);
      setError("Erro ao carregar métricas do Meta Ads");
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar dados quando conta ou data mudam
  useEffect(() => {
    if (selectedAccount && status === "connected") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadCompleteData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAccount,
    dateRange,
    status,
    campaignNameInclude,
    campaignNameExclude,
  ]);

  const handleConnect = async () => {
    try {
      const { data: authData } =
        await getIntegrationAuthUrlApiV1IntegrationsNexusAuthUrlGet({
          query: { source: "meta", alias: "meta-default" },
          throwOnError: true,
        });
      window.location.href = authData.url;
    } catch (err) {
      console.error("Erro ao conectar:", err);
      setError("Erro ao iniciar conexão com Meta Ads");
    }
  };

  // Funções de processamento de dados
  const getMetricsSummary = () => {
    if (!completeData?.daily_metrics?.length) return null;

    const metrics: MetaDailyMetric[] = completeData.daily_metrics;
    const totalSpend = metrics.reduce((sum, m) => sum + (m.cost || 0), 0);
    const totalImpressions = metrics.reduce(
      (sum, m) => sum + (m.impressions || 0),
      0,
    );
    const totalClicks = metrics.reduce((sum, m) => sum + (m.clicks || 0), 0);
    const totalConversions = metrics.reduce(
      (sum, m) => sum + (m.conversions || 0),
      0,
    );
    const avgCtr =
      totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const avgCpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
    const avgRoas =
      totalSpend > 0
        ? metrics.reduce((sum, m) => sum + (m.roas || 0), 0) / metrics.length
        : 0;

    return {
      totalSpend,
      totalImpressions,
      totalClicks,
      totalConversions,
      avgCtr,
      avgCpc,
      avgRoas,
    };
  };

  const getTimeSeriesData = () => {
    if (!completeData?.daily_metrics?.length) return [];

    const dailyMetrics: MetaDailyMetric[] = completeData.daily_metrics;
    const dailyData = dailyMetrics.reduce<Record<string, MetaTimeSeriesDay>>(
      (acc, metric) => {
        const date = metric.date;
        if (!acc[date]) {
          acc[date] = {
            date,
            spend: 0,
            impressions: 0,
            clicks: 0,
            conversions: 0,
            reach: 0,
          };
        }

        acc[date].spend += metric.cost || 0;
        acc[date].impressions += metric.impressions || 0;
        acc[date].clicks += metric.clicks || 0;
        acc[date].conversions += metric.conversions || 0;
        acc[date].reach += metric.reach || 0;

        return acc;
      },
      {},
    );

    return Object.keys(dailyData)
      .map((date) => {
        const day = dailyData[date];
        return {
          ...day,
          ctr: day.impressions > 0 ? (day.clicks / day.impressions) * 100 : 0,
          cpc: day.clicks > 0 ? day.spend / day.clicks : 0,
          date: new Date(day.date).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }),
        };
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const getDemographicsData = () => {
    if (!completeData?.demographics?.length) return [];

    const demographics: MetaDemographicMetric[] = completeData.demographics;
    return demographics.reduce(
      (acc, demo) => {
        const key = `${demo.age || "N/A"} - ${demo.gender || "N/A"}`;
        if (!acc[key]) {
          acc[key] = {
            demographic: key,
            impressions: 0,
            clicks: 0,
            spend: 0,
            conversions: 0,
          };
        }

        acc[key].impressions += demo.impressions || 0;
        acc[key].clicks += demo.clicks || 0;
        acc[key].spend += demo.cost || 0; // Note: cost não spend no modelo
        acc[key].conversions += demo.conversions || 0;

        return acc;
      },
      {} as Record<string, MetaDemographicRow>,
    );
  };

  const getInterestsData = () => {
    if (!completeData?.interests?.length) return [];

    const interests: MetaInterestMetric[] = completeData.interests;
    return interests
      .sort((a, b) => (b.impressions ?? 0) - (a.impressions ?? 0))
      .slice(0, 10);
  };

  const getPlacementData = () => {
    if (!completeData?.placement?.length) return [];

    const placements: MetaPlacementMetric[] = completeData.placement;
    const placementData = placements.reduce<Record<string, MetaPlacementRow>>(
      (acc, placement) => {
        const placementName = placement.platform_position || "Unknown Platform";
        if (!acc[placementName]) {
          acc[placementName] = {
            name: placementName,
            impressions: 0,
            clicks: 0,
            spend: 0,
            conversions: 0,
          };
        }

        acc[placementName].impressions += placement.impressions || 0;
        acc[placementName].clicks += placement.clicks || 0;
        acc[placementName].spend += placement.cost || 0; // Note: cost não spend no modelo
        acc[placementName].conversions += placement.conversions || 0;

        return acc;
      },
      {},
    );

    return Object.keys(placementData).map((name) => {
      const item = placementData[name];
      return {
        ...item,
        ctr: item.impressions > 0 ? (item.clicks / item.impressions) * 100 : 0,
        cpc: item.clicks > 0 ? item.spend / item.clicks : 0,
        color: item.name?.includes("Facebook")
          ? "#1877f2"
          : item.name?.includes("Instagram")
            ? "#e4405f"
            : "#42a5f5",
      };
    });
  };

  const renderConnectionStatus = () => {
    if (status === "disconnected") {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Target className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Conecte ao Meta Ads</h3>
            <p className="text-gray-600 text-center mb-4">
              Conecte sua conta do Meta (Facebook) para analisar suas campanhas
              de anúncios.
            </p>
            <Button onClick={handleConnect} className="flex items-center gap-2">
              <Facebook className="h-4 w-4" />
              Conectar Meta Ads
            </Button>
          </CardContent>
        </Card>
      );
    }

    if (status === "pending") {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Clock className="h-12 w-12 text-yellow-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Aguardando Autorização
            </h3>
            <p className="text-gray-600 text-center">
              Complete a autorização no Meta para conectar sua conta de
              anúncios.
            </p>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  const renderMetricCard = (
    title: string,
    value: number | null | undefined,
    format: (n: number) => string,
    icon: React.ReactNode,
    change?: number,
  ) => (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 flex items-center gap-1">
              <span className="truncate">{title}</span>
              <HelpTooltip
                content={
                  getKpiTooltip(title) ??
                  (title === "Gasto Total"
                    ? getKpiTooltip("Investimento")
                    : undefined)
                }
                triggerClassName="h-3.5 w-3.5 text-[9px]"
              />
            </p>
            <div className="min-w-0">
              <EstadoDoValor
                carregando={loading}
                disponivel={
                  value !== null &&
                  value !== undefined &&
                  Number.isFinite(Number(value))
                }
                fontes={FONTES_DOS_CARDS}
                erro={falhaNasMetricas}
                className="text-2xl font-bold text-muted-foreground"
              >
                <KpiNumber
                  value={Number(value)}
                  format={format}
                  className="font-bold text-foreground"
                  maxFontPx={30}
                  breathingRoomPx={18}
                  ariaLabel={title}
                />
              </EstadoDoValor>
            </div>
            {change !== undefined && (
              <div
                className={`flex items-center gap-1 text-sm ${
                  change > 0
                    ? "text-green-600"
                    : change < 0
                      ? "text-red-600"
                      : "text-gray-600"
                }`}
              >
                {change > 0 ? (
                  <TrendingUp className="h-3 w-3" />
                ) : change < 0 ? (
                  <TrendingDown className="h-3 w-3" />
                ) : null}
                {formatSignedPercent(change, 1)}
              </div>
            )}
          </div>
          <div className="p-2 bg-primary/10 rounded-lg">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );

  if (status === "disconnected" || status === "pending") {
    return (
      <IntegrationErrorBoundary
        integrationContext={{
          type: "meta_ads",
          name: "Meta Ads",
          id: "meta-ads-main",
        }}
      >
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Meta Ads</h1>
              <p className="text-gray-600">
                Gerenciamento e análise das suas campanhas no Facebook e
                Instagram
              </p>
            </div>
          </div>
          {renderConnectionStatus()}
        </div>
      </IntegrationErrorBoundary>
    );
  }

  const summary = getMetricsSummary();
  const timeSeriesData = getTimeSeriesData();
  const demographicsData = getDemographicsData();
  const interestsData = getInterestsData();
  const placementData = getPlacementData();

  const demographicsColumns: TableColumn[] = [
    { key: "demographic", header: "Demografia", type: "text", width: "22ch" },
    {
      key: "impressions",
      header: "Impressões",
      type: "number",
      render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
    },
    {
      key: "clicks",
      header: "Cliques",
      type: "number",
      render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
    },
    {
      key: "spend",
      header: "Gasto",
      type: "currency",
      width: "16ch",
      render: (value) =>
        new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value ?? 0)),
    },
    {
      key: "conversions",
      header: "Conversões",
      type: "number",
      render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
    },
    {
      key: "_ctr",
      header: "CTR",
      type: "percent",
      width: "12ch",
      render: (_value, row) => {
        const typedRow = row as MetaDemographicRow;
        const impressions = Number(typedRow.impressions ?? 0);
        const clicks = Number(typedRow.clicks ?? 0);
        const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
        return `${ctr.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
      },
    },
  ];

  const interestsColumns: TableColumn[] = [
    {
      key: "interest_name",
      header: "Interesse",
      type: "text",
      width: "34ch",
    },
    {
      key: "impressions",
      header: "Impressões",
      type: "number",
      render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
    },
    {
      key: "clicks",
      header: "Cliques",
      type: "number",
      render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
    },
    {
      key: "cost",
      header: "Gasto",
      type: "currency",
      width: "16ch",
      render: (value) =>
        new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value ?? 0)),
    },
    {
      key: "conversions",
      header: "Conversões",
      type: "number",
      render: (value) => Number(value ?? 0).toLocaleString("pt-BR"),
    },
  ];

  return (
    <IntegrationErrorBoundary
      integrationContext={{
        type: "meta_ads",
        name: "Meta Ads",
        id: "meta-ads-main",
      }}
    >
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Meta Ads</h1>
            <p className="text-gray-600">
              Gerenciamento e análise das suas campanhas no Facebook e Instagram
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={forceCheck}
              variant="outline"
              size="sm"
              disabled={statusLoading}
            >
              <RefreshCw
                className={`h-4 w-4 ${statusLoading ? "animate-spin" : ""}`}
              />
            </Button>
            <Badge variant={status === "connected" ? "default" : "secondary"}>
              {status === "connected" ? "Conectado" : "Desconectado"}
            </Badge>
          </div>
        </div>

        {/* Filtro Global */}
        <GlobalFilter className="mb-6" />

        {/* Filtros */}
        <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Conta:</label>
              <Select
                value={selectedAccount}
                onValueChange={setSelectedAccount}
              >
                <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder="Selecione uma conta" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        {account.name || account.external_account_id}
                        <Badge variant="outline" className="text-xs">
                          {account.currency_code || "BRL"}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Período:</label>
              <DatePickerWithRange
                from={dateRange.from}
                to={dateRange.to}
                onUpdate={(range) => setDateRange(range)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">
                Incluir campanhas:
              </label>
              <Input
                placeholder="Ex: institucional, vendas..."
                value={campaignNameInclude}
                onChange={(e) => setCampaignNameInclude(e.target.value)}
                className="h-8"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">
                Excluir campanhas:
              </label>
              <Input
                placeholder="Ex: branding, teste..."
                value={campaignNameExclude}
                onChange={(e) => setCampaignNameExclude(e.target.value)}
                className="h-8"
              />
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Métricas Principais — durante a busca, o indicador fica no lugar de cada número */}
        {(loading || summary) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {renderMetricCard(
              "Gasto Total",
              summary?.totalSpend,
              (n) =>
                new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(n),
              <DollarSign className="h-5 w-5 text-red-600" />,
            )}
            {renderMetricCard(
              "Impressões",
              summary?.totalImpressions,
              (n) => new Intl.NumberFormat("pt-BR").format(Math.trunc(n)),
              <Eye className="h-5 w-5 text-blue-600" />,
            )}
            {renderMetricCard(
              "CTR Médio",
              summary?.avgCtr,
              (n) =>
                `${Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
              <MousePointer className="h-5 w-5 text-green-600" />,
            )}
            {renderMetricCard(
              "ROAS Médio",
              summary?.avgRoas,
              (n) => `${Number(n).toFixed(1)}x`,
              <Target className="h-5 w-5 text-purple-600" />,
            )}
          </div>
        )}

        {/* Sem `completeData` as abas somem; na primeira busca, o aviso fica no lugar delas */}
        {loading && !completeData && <CarregandoConteudo />}

        {completeData && (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid grid-cols-4 w-fit">
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              <TabsTrigger value="demographics">Demografia</TabsTrigger>
              <TabsTrigger value="interests">Interesses</TabsTrigger>
              <TabsTrigger value="placements">Placements</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Gráfico de Performance */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance ao Longo do Tempo</CardTitle>
                  <CardDescription>
                    Evolução das métricas principais das campanhas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ScrollableChart>
                      <TremorLineChart
                        className="h-full"
                        data={timeSeriesData}
                        index="date"
                        categories={[
                          "spend",
                          "impressions",
                          "clicks",
                          "conversions",
                        ]}
                        colors={["red", "blue", "emerald", "violet"]}
                        valueFormatter={(value) =>
                          value.toLocaleString("pt-BR")
                        }
                        showLegend={true}
                        showGridLines={true}
                        showAnimation={true}
                      />
                    </ScrollableChart>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="demographics" className="space-y-6">
              {Object.keys(demographicsData).length > 0 ? (
                <StandardizedTable
                  title="Performance por Demografia"
                  description="Análise de performance por idade e gênero"
                  columns={demographicsColumns}
                  data={Object.values(demographicsData)}
                  loading={loading}
                />
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      Dados Demográficos
                    </h3>
                    <p className="text-gray-600 text-center">
                      Nenhum dado demográfico encontrado para o período
                      selecionado.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="interests" className="space-y-6">
              {interestsData.length > 0 ? (
                <StandardizedTable
                  title="Top Interesses da Audiência"
                  description="Interesses que mais geraram impressões"
                  columns={interestsColumns}
                  data={interestsData}
                  loading={loading}
                />
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <BarChart3 className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      Dados de Interesses
                    </h3>
                    <p className="text-gray-600 text-center">
                      Nenhum dado de interesse encontrado para o período
                      selecionado.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="placements" className="space-y-6">
              {placementData.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Performance por Placement</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <TremorDonutChart
                          className="h-full"
                          data={placementData}
                          index="name"
                          category="impressions"
                          colors={[
                            "blue",
                            "emerald",
                            "amber",
                            "rose",
                            "violet",
                            "cyan",
                          ]}
                          valueFormatter={(value) =>
                            value.toLocaleString("pt-BR")
                          }
                          showLabel={true}
                          showAnimation={true}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Detalhes dos Placements</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {placementData.map((placement, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 border rounded"
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: placement.color }}
                              />
                              <div>
                                <p className="font-medium">{placement.name}</p>
                                <p className="text-sm text-gray-500">
                                  CTR: {(placement.ctr || 0).toFixed(2)}% | CPC:
                                  R$ {(placement.cpc || 0).toFixed(2)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">
                                {(placement.impressions || 0).toLocaleString()}
                              </p>
                              <p className="text-sm text-gray-500">
                                impressões
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <Share2 className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      Dados de Placement
                    </h3>
                    <p className="text-gray-600 text-center">
                      Nenhum dado de placement encontrado para o período
                      selecionado.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </IntegrationErrorBoundary>
  );
};

export default MetaAdsPage;
